
import {Component, Model, Prop, Vue, Watch} from "vue-property-decorator";
import {LocalizationState} from "@/store/modules/LocalizationModule";
import {mapState} from "vuex";

import vToggleSwitch from "./vToggleSwitch.vue";
import vIngredientsDisplayNames from "./vIngredientsDisplayNames.vue";
import vUSContainsPercentOfForm from "./vUSContainsPercentOfForm.vue";
import vAccordion from "./vAccordion.vue";
import {
  CautionaryFrontOfPackRenderData, CautionarySideOfPackRenderData,
  ExportLabelState,
  NutritionalFrontOfPackRenderData
} from "@/models/NutritionLabels/ExportLabelState";
import NutritionLabelRepository from "@/utils/API/NutritionLabelRepository";
import {NutritionLabel} from "@/models/NutritionLabels/NutritionLabel";
import Language from "@/models/Language";
import {Recipe} from "@/models/Recipes/Recipe";
import {RecipeCompositionMeasurement} from "@/models/Recipes/RecipeCompositionMeasurement";
import {debounce, pascalCaseToHuman} from "@/utils/Helpers";
import {DietaryTypes} from "@/models/NutritionLabels/DietaryTypes";
import {LabelTypeMetadata} from "@/models/NutritionLabels/LabelTypeMetadata";
import IngredientDisplay from "@/models/Ingredient/IngredientDisplay";
import {ContainsLessThanPercentOfContent} from "@/models/NutritionLabels/ContainsLessThanPercentOfContent";
import FreeTextPlaceholder from "@/models/NutritionLabels/FreeTextPlaceholder";
import VRecipeNutritionLabelPrintout from "@/components/vRecipeNutritionLabelPrintout.vue";
import VBanner from "@/components/vBanner.vue";
import {RecipeLabel} from "@/models/NutritionLabels/RecipeLabel";
import vCautionaryInputs from "@/components/vCautionaryInputs.vue";
import {buildComplexIngredientName, buildSimpleIngredientName} from "@/services/ExportLabel.service";

@Component({
  components: {
    VRecipeNutritionLabelPrintout,
    VBanner,
    vToggleSwitch,
    vIngredientsDisplayNames,
    vUSContainsPercentOfForm,
    vAccordion,
    vCautionaryInputs
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountStoreState: (state: any) => state.AccountModule,
    }),
  },
})
export default class vBackOfPackageConfig extends Vue {
  LabelTypeMetadata = LabelTypeMetadata;
  localizationState: LocalizationState;

  @Model() model!: ExportLabelState;
  @Prop() recipe!: Recipe;
  @Prop() packageLabelType!: LabelTypeMetadata;
  @Prop() preselectedLabelType: LabelTypeMetadata;
  @Prop() languages!: Language[];
  @Prop({ default: (): RecipeLabel => null }) fetchedLabel: RecipeLabel;

  /**
   * Rounding rule system to use for the given country
   */
  @Prop() roundingRuleIndex!: number;

  @Prop({
    default: (): RecipeCompositionMeasurement[] => {
      return [];
    }
  }) subrecipeMeasurements!: RecipeCompositionMeasurement[];

  nutritionLabels: NutritionLabel[] = [];
  filteredNutritionLabels: NutritionLabel[] = [];
  subRecipeDisplays: IngredientDisplay[] = [];

  selectedLabelType: { name: string, value: LabelTypeMetadata } = null;

  showSupplementFactsLabels: boolean = false;

  @Watch("fetchedLabel")
  async onFetchedLabelChanged(fetchedLabel: RecipeLabel) {
    await this.fetchNutritionLabels();
    this.fillLabelData(fetchedLabel);

    const loadedLabelTypeValues = this.labelTypesFromLoadedLabels().map((lt) => lt.value);
    let selectedLabel = null;

    if(this.model.nutritionLabel){
      selectedLabel = this.nutritionLabels.find((nl) => nl.id == this.model.nutritionLabel.id);
    }
    else{
      selectedLabel = this.nutritionLabels[0];
    }

    if (selectedLabel) {
      this.selectedLabelType = {
        name: pascalCaseToHuman(LabelTypeMetadata[selectedLabel.labelType]),
        value: selectedLabel.labelType
      };

      await this.fetchPreview();
    }

    else if (
      !this.selectedLabelType
      || !loadedLabelTypeValues.includes(this.selectedLabelType.value)
    ) {
      this.selectedLabelType = this.labelTypesFromLoadedLabels()[0];
      await this.updateFilters(this.selectedLabelType);
    }
  }

  // @Watch("selectedLabelType")
  // onSelectedLabelTypeChanged(selectedLabelType: { name: string, value: LabelTypeMetadata }) {
  //   if (selectedLabelType) {
  //     this.updateFilters(selectedLabelType, false);
  //   }
  // }

  // Handle new label flow, no prior saved label
  async created() {
    this.ingredientAndSubRecipeDisplaysInit();
    await this.fetchNutritionLabels();

    if (!this.selectedLabelType && !this.model.nutritionLabel) {
      this.selectedLabelType = {
        name: pascalCaseToHuman(LabelTypeMetadata[this.preselectedLabelType]),
        value: this.preselectedLabelType
      };

      await this.updateFilters(this.selectedLabelType);
      this.updateLabelTypeList(this.selectedLabelType.value);
    }

    else if (this.model.nutritionLabel) {
      const selectedLabel = this.nutritionLabels.find((nl) => nl.id == this.model.nutritionLabel.id);

      if (this.selectedLabelType) {
        await this.updateFilters(this.selectedLabelType);
        this.updateLabelTypeList(this.selectedLabelType.value);
      }

      this.selectedLabelType = {
        name: pascalCaseToHuman(LabelTypeMetadata[selectedLabel.labelType]),
        value: selectedLabel.labelType
      };
    }
  }

  get translations() {
    return this.localizationState.translations;
  }

  get hasOnlyCautionary(){
    if(this.model.hasValidCautionary && this.packageLabelType != LabelTypeMetadata.FrontOfPack){
      return true;
    }
  }

  get ingredientAndSubRecipeDisplays(){
    if(this.model.renderExpandedSubrecipes) {
      return this.model.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList;
    }
    else{
      return this.model.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList;
    }
  }

  get checkedAllergens() {
    return this.model.allergens.filter((x) => x.isChecked == true);
  }

  get dietaryTypes() {
    let types: { name: string; value: number; isChecked: boolean }[] = [];
    for (let dt in DietaryTypes) {
      let isValueProperty: boolean = parseInt(dt, 10) >= 0;
      if (isValueProperty)
        types.push({
          name: this.translations.get(`labels_d_${DietaryTypes[dt]}`),
          value: parseInt(dt),
          isChecked: this.model.dietary.includes(parseInt(dt)),
        });
    }
    return types;
  }

  consolelog(...content: any[]) {
    console.log(...content);
  }

  fillLabelData(labelData: RecipeLabel) {
    this.fillAllergens(labelData.allergens);
    this.fillDietaryTypes(labelData.dietaryTypes);

    this.fillLanguages(labelData.primaryLanguage, labelData.secondaryLanguage);
    this.fillContainsLessThanPercent(labelData.containsLessThanPercentOfContent);
    this.fillIngredientDisplays(
      labelData.ingredientDisplayModels,
      labelData.ingredientOtherDisplayModel,
      labelData.renderExpandedSubrecipes
    );
    this.fillSavedSubRecipeDisplays(labelData.subRecipeDisplayModels);
    this.fillAdditionalProperties(labelData);
    this.fillLabelTypes(labelData);

    if(this.model.nutritionCountry.id == labelData.nutritionCountryId){
      this.assignSavedLabel(labelData.nutritionLabelId);
      this.fillFreeTextPlaceholders(labelData.freeTextPlaceholders);
    }
  }

  //Function to handle allergens.
  private fillAllergens(allergens: any[]) {
    if (allergens && allergens.length > 0) {
      allergens.forEach((x: any) => {
        let a = this.model.allergens.find((y) => y.id === x.id);
        if (a) {
          a.isChecked = x.isChecked;
          a.primaryLanguageName = x.primaryLanguageName;
          a.secondaryLanguageName = x.secondaryLanguageName;
        }
      });
    }
  }

  //Function to handle dietary types.
  private fillDietaryTypes(dietaryTypes: number[]) {
    if (dietaryTypes) {
      this.model.dietary = dietaryTypes;
    }
  }

  // Function to handle nutrition country and label ID.
  private assignSavedLabel(nutritionLabelId: number) {
    this.model.savedNutritionLabelId = nutritionLabelId;

    if (nutritionLabelId > 0) {
      let nutritionLabel = this.nutritionLabels.find(
        (x: any) => x.id == this.model.savedNutritionLabelId
      );

      this.model.nutritionLabel = nutritionLabel ? nutritionLabel : null;
    }
  }


  // Function to handle primary and secondary languages.
  private fillLanguages(primaryLanguage: string, secondaryLanguage: string) {
    if (this.model.primaryLanguage) {
      this.model.primaryLanguage.value = primaryLanguage;
    }
    if (this.model.secondaryLanguage) {
      this.model.secondaryLanguage.value = secondaryLanguage;
    }
  }

  //Function to handle free text placeholders.
  private fillFreeTextPlaceholders(freeTextPlaceholders: FreeTextPlaceholder[]) {
    if (freeTextPlaceholders) {
      this.model.freeTextPlaceholders = freeTextPlaceholders;
      this.model.textPlaceholdersOfSavedLabel = freeTextPlaceholders;
    }
  }

  private fillContainsLessThanPercent(containsLessThanPercent: ContainsLessThanPercentOfContent) {
    if (containsLessThanPercent) {
      this.model.containsLessThanPercentOf = containsLessThanPercent;
    }
  }

  /**
   * Function to handle ingredient displays and subrecipes.
   * @param ingredientDisplayModels DisplayModels from FormBundle, these represent the expanded subrecipes display
   * @param ingredientOtherDisplayModel DisplayModels from FormBundle, these represent the combined subrecipes display
   * @param renderExpandedSubrecipes Whether we should render expanded or combined subrecipes
   * @private
   */
  private fillIngredientDisplays(
    ingredientDisplayModels: any,
    ingredientOtherDisplayModel: any,
    renderExpandedSubrecipes: boolean
  ): void {
    if (ingredientDisplayModels) {
      this.model.savedDisplays = ingredientDisplayModels;
    }

    if (renderExpandedSubrecipes) {
      this.model.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList = this.synchronizeIngredientData(
        this.model.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList,
        ingredientDisplayModels
      );

      if (ingredientOtherDisplayModel) {
        this.model.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList = this.synchronizeRecipeIngredientData(
          this.model.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList,
          ingredientOtherDisplayModel
        );
      }
    }

    else {
      this.model.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList = this.synchronizeRecipeIngredientData(
        this.model.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList,
        ingredientDisplayModels
      );

      // Expanded subrecipes (no subrecipes, only ingredients)
      if (ingredientOtherDisplayModel) {
        this.model.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList = this.synchronizeIngredientData(
          this.model.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList,
          ingredientOtherDisplayModel
        );
      }
    }
  }

  //Function to handle saved subrecipe displays.
  private fillSavedSubRecipeDisplays(subRecipeDisplayModels: any[]) {
    if (subRecipeDisplayModels) {
      this.model.savedSubRecipeDisplays = subRecipeDisplayModels;
    }
  }

  //Function to handle additional properties.
  private fillAdditionalProperties(labelData: any) {
    this.model.includeIngredients = labelData.ingredientsIncluded;
    this.model.mayContain = labelData.additionalContents;
    this.model.renderExpandedSubrecipes = labelData.renderExpandedSubrecipes;

    if (labelData.headerLabelTranslations) {
      this.model.headerLabelTranslations = labelData.headerLabelTranslations;
    }

    if (labelData.cautionaryMessage) {
      this.model.cautionaryMessage = labelData.cautionaryMessage;
    }
  }

  private fillLabelTypes(labelData: RecipeLabel) {
    // NOTE: This is done because during serialization, the enum from C# is cast to a string
    // as such to get the value, we need to convert it back to an enum.
    // @ts-ignore
    const frontOfPackData = labelData.labelTypes[this.labelTypeLookUp(LabelTypeMetadata.FrontOfPack)] as NutritionalFrontOfPackRenderData; // @ts-ignore
    const cautionaryFrontOfPackData = labelData.labelTypes[this.labelTypeLookUp(LabelTypeMetadata.CautionaryFrontOfPack)] as CautionaryFrontOfPackRenderData; // @ts-ignore
    const cautionarySideOfPackData = labelData.labelTypes[this.labelTypeLookUp(LabelTypeMetadata.CautionarySideOfPack)] as CautionarySideOfPackRenderData;

    this.model.labelTypes = {
      [LabelTypeMetadata.FrontOfPack]: {
        fopFormat: 1,// Fallback to 1 as the default value if these aren't present while loading a saved label.
        pdsSize: 1,
        includeInDownload: true,
        ...frontOfPackData
      },
      [LabelTypeMetadata.CautionaryFrontOfPack]: {
        fopFormat: 1,
        pdsSize: 1,
        includeInDownload: true,
        ...cautionaryFrontOfPackData
      },
      [LabelTypeMetadata.CautionarySideOfPack]: {
        foodType: 0,
        caffeinatedType: 0,
        quantityInPackage: 1,
        labelWidth: 450,
        ...cautionarySideOfPackData
      }
    };
  }

  private labelTypeLookUp(labelType: LabelTypeMetadata) {
    const labelTypeMap: { [key in LabelTypeMetadata]: string } = {
      [LabelTypeMetadata.FrontOfPack]: 'frontOfPack',
      [LabelTypeMetadata.CautionaryFrontOfPack]: 'cautionaryFrontOfPack',
      [LabelTypeMetadata.CautionarySideOfPack]: 'cautionarySideOfPack',
      [LabelTypeMetadata.NutritionFacts]: '',
      [LabelTypeMetadata.SupplementFacts]: '',
      [LabelTypeMetadata.FSIS]: ''
    };

    return labelTypeMap[labelType] || '';
  }

  /**
   *
   * @param originalList
   * @param modifiedList
   * @private
   */
  private synchronizeIngredientData(
    originalList: IngredientDisplay[],
    modifiedList: IngredientDisplay[]
  ) {
    const modifiedListMap = new Map<number, IngredientDisplay>();

    modifiedList.forEach((display) => {
      if (display.ingredientId !== undefined) {
        modifiedListMap.set(display.ingredientId, display);
      }
    });

    const updatedSubRecipeList: IngredientDisplay[] = [];
    originalList.forEach((display) => {
      const otherDisplay = modifiedListMap.get(display.ingredientId);
      if (otherDisplay) {
        otherDisplay.weightInGrams = display.weightInGrams;
        updatedSubRecipeList.push(otherDisplay);
        modifiedListMap.delete(display.ingredientId);
      }

      else {
        updatedSubRecipeList.push(display);
      }
    });
    return updatedSubRecipeList;
  }

  /**
   * Generates a list of IngredientDisplays which will be used to represent the ingredients in this recipe.
   * @param originalList Raw ingredient display models generated via the ingredients and subrecipes list of the recipe
   * @param modifiedList Items from the LabelFormBundle. These represent persisted changed values
   * @private
   */
  private synchronizeRecipeIngredientData(
    originalList: IngredientDisplay[],
    modifiedList: IngredientDisplay[]
  ): IngredientDisplay[] {
    const modifiedRecipeListMap = new Map<number, IngredientDisplay>();
    const modifiedIngredientListMap = new Map<number, IngredientDisplay>();

    // Map all changed items, so if an item is modified, it can be accessed via dict
    modifiedList.forEach((display) => {
      if (display.recipeId !== undefined) {
        modifiedRecipeListMap.set(display.recipeId, display);
      }
    });

    // Likewise, we do so for the individual ingredients
    modifiedList.forEach((display) => {
      if (display.ingredientId !== undefined) {
        modifiedIngredientListMap.set(display.ingredientId, display);
      }
    });

    const updatedSubRecipeList: IngredientDisplay[] = [];
    originalList.forEach((originalDisplay) => {
      // Fetch modified values for given display ingredient/subrecipe Id
      const savedRecipeDisplay = modifiedRecipeListMap.get(originalDisplay.recipeId);
      const savedIngredientDisplay = modifiedIngredientListMap.get(originalDisplay.ingredientId);

      // If this is a recipe, and it has been modified, we need to assign the weight in grams so it can be sorted
      if (savedRecipeDisplay && savedRecipeDisplay.recipeId != 0) {
        savedRecipeDisplay.weightInGrams = originalDisplay.weightInGrams;
        updatedSubRecipeList.push(savedRecipeDisplay);
      }

      // Likewise if this is an ingredient, it must have a weight associated so it can be sorted
      else if(savedIngredientDisplay && savedIngredientDisplay.ingredientId != 0) {
        savedIngredientDisplay.weightInGrams = originalDisplay.weightInGrams;
        updatedSubRecipeList.push(savedIngredientDisplay);
      }

      // With no modifications, it is saved as-is
      else {
        updatedSubRecipeList.push(originalDisplay);
      }
    });

    // Return the merged list of DisplayModels
    return updatedSubRecipeList;
  }

  handleCautionaryQualification(state: boolean) {
    this.model.hasValidCautionary = state;
  }

  ingredientAndSubRecipeDisplaysInit() {
    this.subRecipeDisplays = this.model.getSubRecipeDisplays(this.recipe, this.subrecipeMeasurements);

    if(this.model.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList.length == 0){
      this.model.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList = this.model.getIngredientAndSubRecipeDisplays(
        this.recipe,
        this.subrecipeMeasurements,
        true
      );
    }

    if(this.model.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.length == 0){
      this.model.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList = this.model.getIngredientAndSubRecipeDisplays(
        this.recipe,
        this.subrecipeMeasurements,
        false
      );
    }
  }

  /**
   * Resets the display text for a specific subrecipe based on its ID.
   *
   * @param {number} subrecipeId - The unique identifier of the subrecipe to reset its display.
   * @return {void} This method does not return a value.
   */
  resetSubrecipeDisplay(subrecipeId: number) {
    const subrecipeDisplay = this.model.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.find(
      (x) => x.recipeId == subrecipeId
    );

    if (subrecipeDisplay) {
      const recipe = this.recipe.childRecipes.find((x) => x.recipe.id == subrecipeId);
      subrecipeDisplay.displayTextInPrimaryLanguage = buildComplexIngredientName(recipe.recipe);
    }
  }

  resetIngredientDisplay(ingredientId: number) {
    const combinedIngredientDisplay = this.model.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.find(
      (x) => x.ingredientId == ingredientId
    );
    const expandedIngredientDisplay = this.model.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList.find(
      (x) => x.ingredientId == ingredientId
    );

    const recipeIngredient = this.recipe.ingredients.find((x) => x.ingredient.id == ingredientId);

    if (combinedIngredientDisplay) {
      combinedIngredientDisplay.displayTextInPrimaryLanguage = buildSimpleIngredientName(recipeIngredient.ingredient);
    }

    if (expandedIngredientDisplay) {
      expandedIngredientDisplay.displayTextInPrimaryLanguage = buildSimpleIngredientName(recipeIngredient.ingredient);
    }
  }

  /**
   * Identifies and returns a list of ingredients or subrecipes whose display names
   * have been changed from their default or expected values.
   *
   * @return {Array<{type: 'ingredient' | 'subrecipe', id: number}>} An array of objects representing items with changed display names.
   * Each object contains the type ('ingredient' or 'subrecipe') and the corresponding ID of the item.
   */
  get getChangedDisplayNames() {
    const resettableDisplays: { type: 'ingredient' | 'subrecipe'; id: number }[] = [];

    for (const ingredientDisplay of this.model.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList) {
      const isIngredient = Boolean(ingredientDisplay.ingredientId);

      if (isIngredient) {
        const recipeIngredient = this.recipe.ingredients.find((x) => x.ingredient.id == ingredientDisplay.ingredientId);

        if (ingredientDisplay.displayTextInPrimaryLanguage != buildSimpleIngredientName(recipeIngredient.ingredient)) {
          resettableDisplays.push({type: 'ingredient', id: ingredientDisplay.ingredientId});
        }
      }

      else {
        const subrecipe = this.recipe.childRecipes.find((x) => x.recipe.id == ingredientDisplay.recipeId);

        if (ingredientDisplay.displayTextInPrimaryLanguage != buildComplexIngredientName(subrecipe.recipe)) {
          resettableDisplays.push({type: 'subrecipe', id: ingredientDisplay.recipeId});
        }
      }
    }

    return resettableDisplays;
  }

  /**
   * Runs when the user toggles the switch between nutrition facts and supplemented facts
   */
  async updateFilters(selectedLabelType: { name: string, value: LabelTypeMetadata }) {
    this.selectedLabelType = selectedLabelType;
    this.model.nutritionLabelType = selectedLabelType.value;
    this.updateLabelTypeList(selectedLabelType.value);
    // We do this to ensure the selected item is one of the selected label types
    this.resetNutritionLabel();
    // Reset supplement facts config

    this.model.bopCautionaryPreview = "";
    this.model.hasValidCautionary = null;

    await this.fetchFreeTextFields();
    await this.fetchPreview();
  }

  updateLabelTypeList(selectedLabelType: LabelTypeMetadata) {
    this.filteredNutritionLabels = this.nutritionLabels.filter(
      (x: NutritionLabel) => x.labelType == selectedLabelType
    );
  }

  labelTypesFromLoadedLabels() {
    const loadedTypes = new Set(this.nutritionLabels.map((x) => x.labelType));
    const rv: { name: string, value: LabelTypeMetadata }[] = [];

    const sortedTypes = Array.from(loadedTypes).sort((a, b) => {
      return a - b;
    });

    for (const labelType of sortedTypes) {
      const type = LabelTypeMetadata[labelType];
      rv.push({ name: pascalCaseToHuman(type), value: labelType });
    }

    return rv;
  }

  resetNutritionLabel(){
    this.model.nutritionLabel = this.filteredNutritionLabels[0];
  }

  updateAllergenStatus(allergen: any, isChecked: boolean) {
    allergen.isChecked = isChecked;
    this.fetchPreview();
  }

  async fetchNutritionLabels() {
    this.showSupplementFactsLabels = false;

    if (this.recipe.nutritionCountry) {
      const response = await NutritionLabelRepository.listNutritionLabels(
        this.recipe.nutritionCountry.id,
        this.roundingRuleIndex,
        this.roundingRuleIndex > 0
          ? LabelTypeMetadata.FSIS
          : LabelTypeMetadata.NutritionFacts | LabelTypeMetadata.SupplementFacts
      );

      this.nutritionLabels = response.data.result;

      if (this.languages.length) {
        this.model.primaryLanguage = this.languages[0];
      }

      await this.fetchFreeTextFields();
      this.updateLabelTypeList(
        this.roundingRuleIndex > 0
          ? LabelTypeMetadata.FSIS
          : LabelTypeMetadata.NutritionFacts
      );
    }
    else {
      this.model.nutritionLabel = null;
    }
  }

  async onNutritionLabelSelected() {
    this.model.savedNutritionLabelId = 0;
    await this.fetchFreeTextFields();
    await this.fetchPreview();
  }

  async fetchFreeTextFields() {
    if (this.model.nutritionLabel) {
      const response = await NutritionLabelRepository.getFreeTextPlaceholdersForNutritionLabel(
        this.model.nutritionLabel ? this.model.nutritionLabel.id : 0
      );

      if (response.data.result) {
        this.model.freeTextPlaceholders = response.data.result.map((x: any) => {
          const freeTextPlaceholder = this.model.freeTextPlaceholders.find((y: any) => y.id == x.id);

          if (freeTextPlaceholder) {
            x.content = freeTextPlaceholder.content;
          }

          else if (this.model.textPlaceholdersOfSavedLabel != null) {
            /*
            Check to see if the prevouosly saved label has placeholder content,
            then populate the content of the placeholder with its value.
            Covers the scenario when the user has a saved label but shuffles through other ones.
          */
            const savedPlaceholder = this.model.textPlaceholdersOfSavedLabel.find(
              (y: any) => y.id == x.id
            );

            if (savedPlaceholder) {
              x.content = savedPlaceholder.content;
            }
          }

          return x;
        });
      }
    }
  }

  debouncedFetchPreview() {
    debounce(this.fetchPreview, 1000, "back-of-package-config-label-update");
  }

  toggleSubrecipeExpansion() {
    this.model.renderExpandedSubrecipes = !this.model.renderExpandedSubrecipes;

    if(this.model.renderExpandedSubrecipes){
      this.updateIngredientName();
    }
    else{
      this.copyIngredientNamesOver();
    }

    this.debouncedFetchPreview();
  }

  copyIngredientNamesOver(){
    this.model.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList.forEach((expandedItem) => {
      const itemIndex = this.model.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.findIndex((i) => i.ingredientId === expandedItem.ingredientId);

      if (itemIndex !== -1) {
        this.model.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList[itemIndex].displayTextInSecondaryLanguage = expandedItem.displayTextInSecondaryLanguage;
        this.model.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList[itemIndex].displayTextInPrimaryLanguage = expandedItem.displayTextInPrimaryLanguage;
        this.model.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList[itemIndex].displayText = expandedItem.displayText;
      }
    });
  }

  updateIngredientName(){
    this.model.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.forEach((expandedItem) => {
      const itemIndex = this.model.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList.findIndex((i) => i.ingredientId === expandedItem.ingredientId);

      if (itemIndex !== -1) {
        this.model.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList[itemIndex].displayTextInSecondaryLanguage = expandedItem.displayTextInSecondaryLanguage;
        this.model.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList[itemIndex].displayTextInPrimaryLanguage = expandedItem.displayTextInPrimaryLanguage;
        this.model.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList[itemIndex].displayText = expandedItem.displayText;
      }
    });
  }

  toggleDietary(value: number) {
    if (this.model.dietary.includes(value)) {
      this.model.dietary.splice(
        this.model.dietary.indexOf(value),
        1
      );
    }
    else {
      this.model.dietary.push(value);
    }

    this.fetchPreview();
  }

  isSavedLabelPopulated() {
    let labelExists = false;

    if(this.model.savedNutritionLabelId != 0) {
      labelExists = this.nutritionLabels.some(
        (label) => label.id === this.model.savedNutritionLabelId
      );
    }

    if (this.roundingRuleIndex > 0 && !labelExists) {
      return false;
    }

    return true;
  }

  async fetchPreview() {
    if (
      this.model.nutritionCountry &&
      this.model.nutritionLabel &&
      (this.model.savedNutritionLabelId === 0 || (this.model.savedNutritionLabelId > 0 && this.isSavedLabelPopulated()))
    ) {
      if (this.model.nutritionCountry.name === "UK") {
        this.model.headerLabelTranslations.ingredientsSecondaryLanguageTranslation = "";
        this.model.getIngredientDisplays(this.recipe).map((ingredient) => {
          ingredient.displayTextInSecondaryLanguage = "";
        });
      }

      this.model.disableDownload = true;
      this.$emit('label-loading', true);

      const response = await NutritionLabelRepository.previewNutritionLabelForRecipe(
        {
          recipeId: this.recipe.id,
          allergens: this.checkedAllergens,
          nutritionLabelId: this.model.nutritionLabel ? this.model.nutritionLabel.id : 0,
          primaryLanguage: this.model.primaryLanguage ? this.model.primaryLanguage.value : "",
          secondaryLanguage: this.model.secondaryLanguage ? this.model.secondaryLanguage.value : "",
          dietaryTypes: this.model.dietary,
          ingredientsIncluded: this.model.includeIngredients,
          additionalContent: this.model.mayContain,
          cautionaryMessage: this.model.cautionaryMessage,
          containsLessThanPercentOfText: this.model.containsLessThanPercentOf,
          headerTranslations: this.model.headerLabelTranslations,
          nutritionLabelDownloadFileFormat: 0,
          labelWidth: 0,
          labelHeight: 0,
          freeTextPlaceholders: this.model.freeTextPlaceholders,
          ingredientDisplayModels: this.model.renderExpandedSubrecipes
            ? this.model.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList
            : this.model.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList,
        }
      );

      // If request was cancelled, response will be undefined
      if (response === undefined) {
        return;
      }

      this.model.labelPreview = response.data.result;

      this.$emit('label-loading', false);
      this.model.disableDownload = false;
    }
  }
}
